'use client';

/**
 * PostHog Analytics Provider — GDPR-compliant, true opt-in.
 *
 * PostHog does NOT initialize until the user explicitly accepts analytics
 * via the consent banner or settings toggle. Before acceptance:
 *   - No PostHog cookies are set
 *   - No network requests to PostHog servers
 *   - No session recording, heatmaps, or autocapture
 *   - trackEvent() and all other helpers silently no-op
 *
 * On acceptance: PostHog initializes, identifies the user, and begins
 * capturing events. On decline or revocation: PostHog tears down and
 * clears its cookies.
 *
 * Consent state is read from a first-party cookie (see src/lib/consent.ts).
 * Changes are reactive — the consent banner calls writeConsent() which
 * notifies this provider via onConsentChange().
 *
 * Environment variables:
 *   NEXT_PUBLIC_POSTHOG_KEY  — PostHog project API key
 *   NEXT_PUBLIC_POSTHOG_HOST — PostHog instance URL (default: managed proxy)
 */

import { useEffect, useRef, useCallback } from 'react';
import posthog from 'posthog-js';
import { usePathname, useSearchParams } from 'next/navigation';
import { hasAnalyticsConsent, onConsentChange, type ConsentState } from '@/lib/consent';
import { logInfo } from '@/lib/client-logger';

// ─── PostHog Initialization ─────────────────────────────────────────────

function initPostHog(): boolean {
  const key = process.env.NEXT_PUBLIC_POSTHOG_KEY;
  const host = process.env.NEXT_PUBLIC_POSTHOG_HOST || 'https://e.serenoty.com';
  if (!key) return false;

  try {
    posthog.init(key, {
      api_host: host,
      ui_host: 'https://us.posthog.com',
      person_profiles: 'identified_only',
      capture_pageview: false,
      capture_pageleave: true,
      persistence: 'cookie',
      autocapture: false,
      respect_dnt: true,
      property_denylist: ['$ip'],

      // Do NOT start opted-in — we control opt-in via consent.ts.
      // This prevents PostHog from setting any cookies or making any
      // network requests until we explicitly call opt_in_capturing().
      opt_out_capturing_by_default: true,

      disable_session_recording: false,
      session_recording: {
        maskAllInputs: true,
        maskTextSelector: '*',
        recordCrossOriginIframes: false,
      },
      enable_heatmaps: true,

      loaded: (ph) => {
        ph.register_once({ service: 'nova' });
        // Now opt in — we only reach this code path when consent === 'accepted'
        ph.opt_in_capturing();
        logInfo('posthog.initialized', { distinct_id: ph.get_distinct_id() });
        if (process.env.NODE_ENV === 'development') {
          ph.debug();
        }
      },
    });
    return true;
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.warn('[PostHog] Failed to initialize:', error);
    }
    return false;
  }
}

function teardownPostHog(): void {
  try {
    if (posthog && typeof posthog.opt_out_capturing === 'function') {
      posthog.opt_out_capturing();
    }
    if (posthog && typeof posthog.reset === 'function') {
      posthog.reset();
    }
    logInfo('posthog.teardown');
  } catch {
    // PostHog may not be initialized — safe to ignore
  }
}

// ─── Provider Component ─────────────────────────────────────────────────

export function PostHogProvider({ children }: { children: React.ReactNode }) {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const initialized = useRef(false);

  // Handle consent state changes reactively. When the user accepts via the
  // banner or settings toggle, writeConsent('accepted') fires, which calls
  // our listener → we initialize PostHog. On decline → teardown.
  const handleConsentChange = useCallback((state: ConsentState) => {
    if (state === 'accepted' && !initialized.current) {
      initialized.current = initPostHog();
    } else if (state === 'declined' && initialized.current) {
      teardownPostHog();
      initialized.current = false;
    }
  }, []);

  useEffect(() => {
    // On mount: if consent was already accepted (returning user with cookie),
    // initialize immediately.
    if (hasAnalyticsConsent() && !initialized.current) {
      initialized.current = initPostHog();
    }

    // Subscribe to future consent changes (accept/decline during this session)
    const unsub = onConsentChange(handleConsentChange);
    return unsub;
  }, [handleConsentChange]);

  // Track pageviews on route changes — only when initialized and consented
  useEffect(() => {
    if (!initialized.current || !hasAnalyticsConsent()) return;
    const url = pathname + (searchParams?.toString() ? `?${searchParams.toString()}` : '');
    posthog.capture('$pageview', { $current_url: url });
  }, [pathname, searchParams]);

  return <>{children}</>;
}

// ─── Analytics Helpers ──────────────────────────────────────────────────
// All helpers re-export hasAnalyticsConsent from consent.ts so existing
// imports continue to work. The consent gate is now cookie-based, not
// PostHog's internal opt-out state.

export { hasAnalyticsConsent } from '@/lib/consent';

/** For test overrides — tests can set this to bypass the cookie check. */
export const _test = { consentOverride: null as (() => boolean) | null };

/** Internal consent check that respects test overrides. */
function _checkConsent(): boolean {
  if (_test.consentOverride) return _test.consentOverride();
  return hasAnalyticsConsent();
}

/**
 * Track a custom event with PostHog.
 * Silently no-ops if PostHog isn't initialized, blocked, or user hasn't consented.
 */
export function trackEvent(event: string, properties?: Record<string, unknown>) {
  if (typeof window === 'undefined') return;
  if (!_checkConsent()) return;
  try {
    if (posthog && typeof posthog.capture === 'function') {
      posthog.capture(event, properties);
    }
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.debug(`[PostHog] Event "${event}" not captured:`, error);
    }
  }
}

/** Identify a user in PostHog. */
export function identifyUser(userId: string, properties?: Record<string, unknown>) {
  if (typeof window === 'undefined') return;
  try {
    if (posthog && typeof posthog.identify === 'function') {
      posthog.identify(userId, properties);
    }
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.debug(`[PostHog] Failed to identify user:`, error);
    }
  }
}

/** Reset PostHog identity (on sign out). */
export function resetAnalytics() {
  if (typeof window === 'undefined') return;
  try {
    if (posthog && typeof posthog.reset === 'function') {
      posthog.reset();
    }
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.debug(`[PostHog] Failed to reset analytics:`, error);
    }
  }
}

/**
 * Associate the current user with a workspace group in PostHog.
 * Call on workspace switch so events are attributed to the correct workspace.
 */
export function setWorkspaceGroup(workspaceId: string, properties?: Record<string, unknown>) {
  if (typeof window === 'undefined') return;
  try {
    if (posthog && typeof posthog.group === 'function') {
      posthog.group('workspace', workspaceId, properties);
    }
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.debug(`[PostHog] Failed to set workspace group:`, error);
    }
  }
}

/** Get the PostHog distinct_id for Sentry linkage. */
export function getDistinctId(): string | undefined {
  if (typeof window === 'undefined') return undefined;
  try {
    if (posthog && typeof posthog.get_distinct_id === 'function') {
      return posthog.get_distinct_id();
    }
  } catch {
    return undefined;
  }
  return undefined;
}

/**
 * Track a feature flag/experiment exposure.
 * Automatically deduplicates per session to avoid double-counting.
 */
export function trackExposure(experimentKey: string, variant: string, surface?: string) {
  if (typeof window === 'undefined') return;
  if (!_checkConsent()) return;
  try {
    if (posthog && typeof posthog.capture === 'function') {
      posthog.capture('$feature_flag_called', {
        $feature_flag: experimentKey,
        $feature_flag_response: variant,
        ...(surface && { surface }),
      });
    }
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.debug(`[PostHog] Exposure "${experimentKey}" not tracked:`, error);
    }
  }
}

/**
 * Track an experiment outcome event.
 */
export function trackOutcome(eventName: string, properties?: Record<string, unknown>) {
  if (typeof window === 'undefined') return;
  if (!_checkConsent()) return;
  try {
    if (posthog && typeof posthog.capture === 'function') {
      posthog.capture(eventName, properties);
    }
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.debug(`[PostHog] Outcome "${eventName}" not tracked:`, error);
    }
  }
}
